import { useEffect, useMemo, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { Link } from 'react-router';
import { Receipt } from 'lucide-react';
import type { ColumnDef, RowSelectionState, SortingState, Updater } from '@tanstack/react-table';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { graphql } from '@/gql';
import type {
  BulkPerformOrderActionMutation,
  BulkPerformOrderActionMutationVariables,
  DevicesForOrdersFilterQuery,
  DiscountsForOrdersFilterQuery,
  ExportOrdersCsvQuery,
  ExportOrdersCsvQueryVariables,
  OrderAction,
  OrderFilter,
  OrdersQuery,
  OrdersQueryVariables,
  OrderStatus,
  SortDirection,
  ProductsForOrdersFilterQuery,
  TablesForOrdersFilterQuery,
} from '@/gql/graphql';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { DataGrid } from '@/components/ui/DataGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { formatCurrency, formatDateTime, formatShortId } from '@/lib/format';
import { paymentMethodLabel } from '@/lib/labels';
import { downloadAuthenticatedCsv } from '@/lib/downloadCsv';
import { cn } from '@/lib/cn';
import { useListHref } from '@/hooks/useListLocation';
import {
  isDefault,
  sortingFromUrl,
  sortingToUrl,
  useDebouncedUrlField,
  useUrlState,
} from '@/hooks/useUrlState';
import {
  STAFF_RECEIPT_GRID_BUTTON_CLASS,
  openStaffOrderReceipt,
} from '@/lib/staffReceiptDownload';
import {
  ORDER_ACTION_LABELS,
  ORDER_BULK_ACTIONS,
  orderActionVariant,
} from './orderActions';
import {
  ORDER_COLUMN_SORT,
  ORDER_METHOD_OPTIONS,
  ORDER_STATUS_OPTIONS,
  ORDERS_ADVANCED_KEYS,
  ORDERS_DEFAULT_SORT,
  ORDERS_URL_ALIASES,
  ordersUrlSchema,
  type OrderMethod,
} from './ordersUrlState';

const OrdersDoc = graphql(/* GraphQL */ `
  query Orders(
    $filter: OrderFilter
    $page: Int
    $pageSize: Int
    $sort: OrderSort
  ) {
    orders(filter: $filter, page: $page, pageSize: $pageSize, sort: $sort) {
      nodes {
        id
        status
        paymentStatus
        paymentMethod
        createdAt
        updatedAt
        grandTotal
        amountPaid
        amountDue
        allowedActions
        discount {
          id
          name
        }
        device {
          id
          name
        }
        table {
          id
          name
        }
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`) as TypedDocumentNode<OrdersQuery, OrdersQueryVariables>;

const ExportOrdersCsvDoc = graphql(/* GraphQL */ `
  query ExportOrdersCsv($filter: OrderFilter, $sort: OrderSort) {
    exportOrdersCsv(filter: $filter, sort: $sort) {
      url
      expiresAt
    }
  }
`) as TypedDocumentNode<ExportOrdersCsvQuery, ExportOrdersCsvQueryVariables>;

const BulkPerformOrderActionDoc = graphql(/* GraphQL */ `
  mutation BulkPerformOrderAction($ids: [ID!]!, $action: OrderAction!) {
    bulkPerformOrderAction(ids: $ids, action: $action) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`) as TypedDocumentNode<
  BulkPerformOrderActionMutation,
  BulkPerformOrderActionMutationVariables
>;

const DevicesForOrdersFilterDoc = graphql(/* GraphQL */ `
  query DevicesForOrdersFilter {
    devices(filter: {}, includeInactive: true, page: 1, pageSize: 500) {
      nodes {
        id
        name
      }
    }
  }
`) as TypedDocumentNode<DevicesForOrdersFilterQuery>;

const TablesForOrdersFilterDoc = graphql(/* GraphQL */ `
  query TablesForOrdersFilter {
    tables(includeInactive: true) {
      id
      name
    }
  }
`) as TypedDocumentNode<TablesForOrdersFilterQuery>;

const DiscountsForOrdersFilterDoc = graphql(/* GraphQL */ `
  query DiscountsForOrdersFilter {
    discounts(
      filter: {}
      includeInactive: true
      page: 1
      pageSize: 500
    ) {
      nodes {
        id
        name
      }
    }
  }
`) as TypedDocumentNode<DiscountsForOrdersFilterQuery>;

const ProductsForOrdersFilterDoc = graphql(/* GraphQL */ `
  query ProductsForOrdersFilter(
    $filter: ProductFilter
    $includeInactive: Boolean!
    $page: Int!
    $pageSize: Int!
  ) {
    products(
      filter: $filter
      includeInactive: $includeInactive
      page: $page
      pageSize: $pageSize
    ) {
      nodes {
        id
        name
      }
    }
  }
`) as TypedDocumentNode<ProductsForOrdersFilterQuery>;

interface OrderRow {
  id: string;
  status: OrderStatus;
  paymentStatus: string;
  paymentMethod: string;
  createdAt: string;
  updatedAt: string;
  grandTotal: number;
  amountPaid: number;
  amountDue: number;
  allowedActions: OrderAction[];
  discount?: { id: string; name: string } | null;
  device?: { id: string; name: string } | null;
  table?: { id: string; name: string } | null;
}

const STATUS_TONE: Record<OrderStatus, BadgeTone> = {
  NEW: 'neutral',
  OPEN: 'info',
  PENDING: 'info',
  CANCELING: 'warning',
  CANCELED: 'neutral',
  LOSS: 'error',
  FAILED: 'error',
  PARTIAL_PAID: 'warning',
  FULL_PAID: 'success',
  REFUNDING: 'warning',
  PARTIAL_REFUNDED: 'warning',
  FULL_REFUNDED: 'warning',
  REFUNDED: 'warning',
};

function multiSelectClassName(): string {
  return cn(
    'block w-full min-h-[11rem] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-xs',
    'focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-200',
    'dark:border-gray-700 dark:bg-gray-900 dark:text-white dark:focus:ring-brand-900/60',
  );
}

const filterSectionTitle =
  'mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';
const filterLabel = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200';
const filterHint = 'mt-1.5 text-xs leading-snug text-gray-500 dark:text-gray-400';

/**
 * Orders list — paginated DataGrid with filters and CSV export.
 */
export function OrdersListPage() {
  const apollo = useApolloClient();
  const { state: url, patch, setFilters, reset } = useUrlState(ordersUrlSchema, { aliases: ORDERS_URL_ALIASES });
  const listHref = useListHref();
  const [search, setSearch] = useDebouncedUrlField(url.search, (next) => setFilters({ search: next }));
  const [totalMin, setTotalMin] = useDebouncedUrlField(url.totalMin, (next) => setFilters({ totalMin: next }));
  const [totalMax, setTotalMax] = useDebouncedUrlField(url.totalMax, (next) => setFilters({ totalMax: next }));
  const [dueMin, setDueMin] = useDebouncedUrlField(url.dueMin, (next) => setFilters({ dueMin: next }));
  const [dueMax, setDueMax] = useDebouncedUrlField(url.dueMax, (next) => setFilters({ dueMax: next }));
  // The Filters panel opens on mount when a filter inside it is set (a reload, Back, an inbound link).
  const [filtersOpen] = useState(() => ORDERS_ADVANCED_KEYS.some((key) => !isDefault(ordersUrlSchema, url, key)));
  const [productSearchInput, setProductSearchInput] = useState('');
  const [debouncedProductSearch, setDebouncedProductSearch] = useState('');
  const [selection, setSelection] = useState<RowSelectionState>({});
  const sorting = useMemo(() => sortingFromUrl(url), [url]);
  const handleSortingChange = (updater: Updater<SortingState>) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setFilters(sortingToUrl(next, ORDERS_DEFAULT_SORT));
  };
  const [feedback, setFeedback] = useState<string | null>(null);
  const [bulkPerformOrderAction] = useMutation(BulkPerformOrderActionDoc);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedProductSearch(productSearchInput), 400);
    return () => window.clearTimeout(t);
  }, [productSearchInput]);

  const { data: devicesData } = useQuery(DevicesForOrdersFilterDoc);
  const { data: tablesData } = useQuery(TablesForOrdersFilterDoc);
  const { data: discountsData } = useQuery(DiscountsForOrdersFilterDoc);
  const { data: productsData } = useQuery(ProductsForOrdersFilterDoc, {
    variables: {
      filter: {
        search: debouncedProductSearch.trim() || null,
      },
      includeInactive: true,
      page: 1,
      pageSize: 100,
    },
  });

  const filter: OrderFilter = useMemo(() => {
    const out: OrderFilter = {
      search: url.search.trim() || null,
      status: url.status ? [url.status] : null,
      paymentMethod: url.method ? [url.method] : null,
    };

    const rf = url.from ? `${url.from}T00:00:00` : null;
    const rt = url.to ? `${url.to}T23:59:59` : null;
    if (rf || rt) {
      out.recognitionDate = { from: rf, to: rt };
    }

    if (url.totalMin !== '' || url.totalMax !== '') {
      out.amount = {
        min: url.totalMin === '' ? null : Number(url.totalMin),
        max: url.totalMax === '' ? null : Number(url.totalMax),
      };
    }

    if (url.dueMin !== '' || url.dueMax !== '') {
      out.amountDue = {
        min: url.dueMin === '' ? null : Number(url.dueMin),
        max: url.dueMax === '' ? null : Number(url.dueMax),
      };
    }

    if (url.devices.length > 0) {
      out.deviceIds = [...url.devices];
    }
    if (url.tables.length > 0) {
      out.tableIds = [...url.tables];
    }

    if (url.discounts.length > 0) {
      out.discountIds = [...url.discounts];
    }

    if (url.products.length > 0) {
      out.productIds = [...url.products];
    }

    return out;
  }, [url]);

  const gqlSort = useMemo(
    () => ({
      field: ORDER_COLUMN_SORT[url.sort],
      direction: (url.dir === 'asc' ? 'ASC' : 'DESC') as SortDirection,
    }),
    [url.sort, url.dir],
  );

  const { data, previousData, loading, error } = useQuery(OrdersDoc, {
    variables: { filter, page: url.page, pageSize: url.size, sort: gqlSort },
  });
  const ordersData = data ?? previousData;

  const rows: OrderRow[] = useMemo(
    () =>
      (ordersData?.orders.nodes ?? []).map((n) => ({
        id: n.id,
        status: n.status,
        paymentStatus: n.paymentStatus,
        paymentMethod: n.paymentMethod ?? '',
        createdAt: n.createdAt,
        updatedAt: n.updatedAt,
        grandTotal: n.grandTotal,
        amountPaid: n.amountPaid,
        amountDue: n.amountDue,
        allowedActions: n.allowedActions,
        discount: n.discount,
        device: n.device,
        table: n.table,
      })),
    [ordersData],
  );
  const pageInfo = ordersData?.orders.pageInfo;

  const columns = useMemo<ColumnDef<OrderRow>[]>(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: 'Order',
        cell: ({ row }) => (
          <Link
            to={`/orders/${row.original.id}`}
            state={{ from: listHref }}
            className="font-medium text-gray-900 hover:text-brand dark:text-white"
          >
            #{formatShortId(row.original.id)}
          </Link>
        ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge tone={STATUS_TONE[row.original.status] ?? 'neutral'}>
            {row.original.status.replace('_', ' ').toLowerCase()}
          </Badge>
        ),
      },
      {
        id: 'method',
        accessorKey: 'paymentMethod',
        header: 'Method',
        cell: ({ row }) => paymentMethodLabel(row.original.paymentMethod),
      },
      {
        id: 'device',
        accessorFn: (row) => row.device?.name ?? '',
        header: 'Device',
        cell: ({ row }) => row.original.device?.name ?? '—',
      },
      {
        id: 'table',
        accessorFn: (row) => row.table?.name ?? '',
        header: 'Table',
        cell: ({ row }) => row.original.table?.name ?? '—',
      },
      {
        id: 'discount',
        accessorFn: (row) => row.discount?.name ?? '',
        header: 'Discount',
        cell: ({ row }) => {
          const d = row.original.discount;
          if (!d) return '—';
          return (
            <Link
              to={`/discounts/${d.id}`}
              className="text-brand hover:underline truncate max-w-[14rem] inline-block align-bottom"
            >
              {d.name}
            </Link>
          );
        },
      },
      {
        id: 'total',
        accessorKey: 'grandTotal',
        header: 'Total',
        cell: ({ row }) => formatCurrency(row.original.grandTotal),
      },
      {
        id: 'due',
        accessorKey: 'amountDue',
        header: 'Amount due',
        cell: ({ row }) => (
          <span className={row.original.amountDue > 0 ? 'text-red-600 font-medium' : ''}>
            {formatCurrency(row.original.amountDue)}
          </span>
        ),
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
    ],
    [listHref],
  );

  const clearAllFilters = () => {
    reset();
    setProductSearchInput('');
  };

  const handleExport = async () => {
    const { data: exportData } = await apollo.query({
      query: ExportOrdersCsvDoc,
      variables: { filter, sort: gqlSort },
      fetchPolicy: 'network-only',
    });
    if (exportData?.exportOrdersCsv?.url) {
      await downloadAuthenticatedCsv(exportData.exportOrdersCsv.url);
    }
  };

  const runBulk = async (ids: string[], action: OrderAction) => {
    if (ids.length === 0) return;
    setFeedback(null);
    try {
      const { data: result } = await bulkPerformOrderAction({
        variables: { ids, action },
      });
      const failed = result?.bulkPerformOrderAction.failed ?? [];
      if (failed.length > 0) {
        const first = failed[0]?.reason ?? 'Unknown error';
        setFeedback(
          `${failed.length} order(s) failed during "${ORDER_ACTION_LABELS[action]}". First error: ${first}`,
        );
      } else {
        setFeedback(`Applied "${ORDER_ACTION_LABELS[action]}" to ${ids.length} order(s).`);
      }
      await apollo.refetchQueries({ include: [OrdersDoc] });
      setSelection({});
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Bulk action failed.');
    }
  };

  const deviceNodes = devicesData?.devices.nodes ?? [];
  const tableNodes = tablesData?.tables ?? [];
  const discountNodes = discountsData?.discounts.nodes ?? [];
  const productNodes = productsData?.products.nodes ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Orders</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Every order within the current scope. Click one to drill into items, payments and refunds.
          </p>
        </div>
      </div>

      <FilterBar
        defaultOpen={filtersOpen}
        search={{
          id: 'admin.orders.search',
          value: search,
          onChange: setSearch,
          placeholder: 'Search by id or payment method…',
        }}
        primary={
          <button
            type="button"
            onClick={clearAllFilters}
            className="whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-xs transition-colors hover:bg-gray-50 dark:border-white/10 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            Reset all filters
          </button>
        }
        advanced={
          <div className="w-full min-w-0 space-y-10">
            <section className="min-w-0" aria-labelledby="orders-filters-basics-heading">
              <h3 id="orders-filters-basics-heading" className={filterSectionTitle}>
                Status, payment and recognition date
              </h3>
              <div className="grid w-full grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-12">
                <label className="min-w-0 sm:col-span-1 lg:col-span-2">
                  <span className={filterLabel}>Status</span>
                  <Select
                    className="w-full min-w-0"
                    id="admin.orders.status"
                    value={url.status}
                    onChange={(event) => setFilters({ status: event.target.value as OrderStatus | '' })}
                    options={ORDER_STATUS_OPTIONS}
                  />
                </label>
                <label className="min-w-0 sm:col-span-1 lg:col-span-2">
                  <span className={filterLabel}>Payment method</span>
                  <Select
                    className="w-full min-w-0"
                    id="admin.orders.method"
                    value={url.method}
                    onChange={(event) => setFilters({ method: event.target.value as OrderMethod | '' })}
                    options={ORDER_METHOD_OPTIONS}
                  />
                </label>
                <label className="min-w-0 sm:col-span-2 lg:col-span-8">
                  <span className={filterLabel}>Recognition date (from → to)</span>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
                    <Input
                      type="date"
                      value={url.from}
                      onChange={(e) => setFilters({ from: e.target.value })}
                      className="min-h-[2.5rem] w-full min-w-0 flex-1"
                    />
                    <span
                      className="hidden shrink-0 self-center text-sm text-gray-400 sm:inline dark:text-gray-500"
                      aria-hidden
                    >
                      →
                    </span>
                    <Input
                      type="date"
                      value={url.to}
                      onChange={(e) => setFilters({ to: e.target.value })}
                      className="min-h-[2.5rem] w-full min-w-0 flex-1"
                    />
                  </div>
                  <span className={filterHint}>
                    {
                      "Uses each order's recognition (business) date, not when the order record was first created."
                    }
                  </span>
                </label>
              </div>
            </section>

            <section
              className="min-w-0 border-t border-gray-200/90 pt-8 dark:border-white/10"
              aria-labelledby="orders-filters-amounts-heading"
            >
              <h3 id="orders-filters-amounts-heading" className={filterSectionTitle}>
                Order totals
              </h3>
              <div className="grid w-full grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-4 xl:grid-cols-6">
                <label className="min-w-0">
                  <span className={filterLabel}>Grand total min</span>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={totalMin}
                    onChange={(e) => setTotalMin(e.target.value)}
                    placeholder="Any"
                    className="w-full min-w-0"
                  />
                </label>
                <label className="min-w-0">
                  <span className={filterLabel}>Grand total max</span>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={totalMax}
                    onChange={(e) => setTotalMax(e.target.value)}
                    placeholder="Any"
                    className="w-full min-w-0"
                  />
                </label>
                <label className="min-w-0">
                  <span className={filterLabel}>Amount due min</span>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={dueMin}
                    onChange={(e) => setDueMin(e.target.value)}
                    placeholder="Any"
                    className="w-full min-w-0"
                  />
                </label>
                <label className="min-w-0">
                  <span className={filterLabel}>Amount due max</span>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={dueMax}
                    onChange={(e) => setDueMax(e.target.value)}
                    placeholder="Any"
                    className="w-full min-w-0"
                  />
                </label>
              </div>
            </section>

            <section
              className="min-w-0 border-t border-gray-200/90 pt-8 dark:border-white/10"
              aria-labelledby="orders-filters-place-heading"
            >
              <h3 id="orders-filters-place-heading" className={filterSectionTitle}>
                Device, table and discounts
              </h3>
              <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
                <label className="flex min-w-0 flex-col">
                  <span className={filterLabel}>Device</span>
                  <select
                    multiple
                    className={multiSelectClassName()}
                    value={url.devices}
                    onChange={(e) =>
                      setFilters({ devices: Array.from(e.target.selectedOptions, (opt) => opt.value) })
                    }
                    aria-label="Filter by device"
                  >
                    {deviceNodes.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                  <span className={filterHint}>Hold Cmd (Mac) or Ctrl (Windows) while clicking to select several.</span>
                </label>
                <label className="flex min-w-0 flex-col">
                  <span className={filterLabel}>Table</span>
                  <select
                    multiple
                    className={multiSelectClassName()}
                    value={url.tables}
                    onChange={(e) =>
                      setFilters({ tables: Array.from(e.target.selectedOptions, (opt) => opt.value) })
                    }
                    aria-label="Filter by table"
                  >
                    {tableNodes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <span className={filterHint}>Same multi-select gesture as for devices.</span>
                </label>
                <label className="flex min-w-0 flex-col">
                  <span className={filterLabel}>Discount</span>
                  <select
                    multiple
                    className={multiSelectClassName()}
                    value={url.discounts}
                    onChange={(e) =>
                      setFilters({ discounts: Array.from(e.target.selectedOptions, (opt) => opt.value) })
                    }
                    aria-label="Filter by discount"
                  >
                    {discountNodes.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                  <span className={filterHint}>
                    Orders whose order-level discount is one of the selected promotions. Cmd/Ctrl-click to
                    select several.
                  </span>
                </label>
              </div>
            </section>

            <section
              className="min-w-0 border-t border-gray-200/90 pt-8 dark:border-white/10"
              aria-labelledby="orders-filters-products-heading"
            >
              <h3 id="orders-filters-products-heading" className={filterSectionTitle}>
                Products on the order
              </h3>
              <p className="mb-4 max-w-3xl text-sm text-gray-600 dark:text-gray-400">
                Only orders that contain <strong className="font-medium text-gray-800 dark:text-gray-200">every</strong>{' '}
                selected product (line items) are shown. Search to find products, then multi-select.
              </p>
              <div className="flex max-w-4xl flex-col gap-3">
                <Input
                  type="search"
                  value={productSearchInput}
                  onChange={(e) => setProductSearchInput(e.target.value)}
                  placeholder="Search products by name…"
                  className="w-full"
                />
                <select
                  multiple
                  className={multiSelectClassName()}
                  value={url.products}
                  onChange={(e) =>
                    setFilters({ products: Array.from(e.target.selectedOptions, (opt) => opt.value) })
                  }
                  aria-label="Filter by products on order"
                >
                  {productNodes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <span className={filterHint}>Cmd/Ctrl-click to combine multiple products.</span>
              </div>
            </section>
          </div>
        }
      />

      {feedback && (
        <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-2 text-sm text-brand-700 dark:border-brand-800 dark:bg-brand-900/40 dark:text-brand-200">
          {feedback}
        </div>
      )}

      {error ? <QueryErrorNotice error={error} title="Could not load orders" /> : null}

      <DataGrid<OrderRow, OrderAction>
        data={rows}
        columns={columns}
        loading={loading && !ordersData}
        rowId={(row) => row.id}
        sorting={{ state: sorting, onChange: handleSortingChange }}
        onExport={handleExport}
        selection={{ state: selection, onChange: setSelection }}
        allowedActionsForRow={(row) => row.allowedActions}
        actions={[
          ...ORDER_BULK_ACTIONS.map((action) => ({
            id: action,
            label: ORDER_ACTION_LABELS[action],
            variant: orderActionVariant(action),
            onRun: (selected: OrderRow[]) => runBulk(selected.map((row) => row.id), action),
          })),
          {
            id: 'DOWNLOAD_RECEIPT',
            label: ORDER_ACTION_LABELS.DOWNLOAD_RECEIPT,
            variant: 'outline',
            icon: <Receipt className="h-4 w-4" aria-hidden />,
            buttonClassName: STAFF_RECEIPT_GRID_BUTTON_CLASS,
            isAllowed: (selected: OrderRow[]) =>
              selected.length === 1 &&
              selected[0].allowedActions.includes('DOWNLOAD_RECEIPT'),
            onRun: async (selected: OrderRow[]) => {
              setFeedback(null);
              try {
                await openStaffOrderReceipt(apollo, selected[0].id);
              } catch (e) {
                setFeedback(e instanceof Error ? e.message : 'Receipt download failed.');
              }
            },
          },
        ]}
        pagination={{
          page: pageInfo?.page ?? url.page,
          pageSize: pageInfo?.pageSize ?? url.size,
          totalRows: pageInfo?.totalRows ?? 0,
          totalPages: pageInfo?.totalPages ?? 1,
          onPageChange: (next) => patch({ page: next }),
          onPageSizeChange: (next) => setFilters({ size: next }),
        }}
      />
    </div>
  );
}
